import type { CSSProperties, Ref } from "react";
import { NavLink } from "react-router-dom";
import type { NavItem } from "./nav";

interface NavRowProps {
  item: NavItem;
  /** Extra classes — the drawer uses them to slide the row in. */
  className?: string;
  style?: CSSProperties;
  linkRef?: Ref<HTMLAnchorElement>;
}

/**
 * One section link, shared by the desktop sidebar and the phone drawer.
 *
 * They are the same row in two frames, so the active state and the accent
 * marker live here rather than being written twice and drifting apart.
 */
export default function NavRow({ item, className = "", style, linkRef }: NavRowProps) {
  return (
    <NavLink
      ref={linkRef}
      to={item.to}
      end={item.end}
      style={style}
      className={({ isActive }) =>
        [
          "group relative flex items-center gap-3 rounded-control py-2.5 pr-3 pl-4 text-sm",
          "transition-[background-color,color,opacity,transform] duration-300 ease-drawer",
          isActive
            ? "bg-accent-soft font-medium text-fg"
            : "text-muted hover:bg-surface-2 hover:text-fg",
          className,
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={`absolute top-1/2 left-0 h-5 w-0.5 -translate-y-1/2 rounded-full bg-accent transition-transform duration-200 ${
              isActive ? "scale-y-100" : "scale-y-0"
            }`}
          />
          <item.Icon
            className={`size-[18px] shrink-0 transition-colors ${
              isActive ? "text-accent" : "text-muted group-hover:text-fg"
            }`}
          />
          <span className="flex-1 truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}
